// ABOUTME: Component for managing user authentication providers (Google, Facebook, Email)
// ABOUTME: Allows users to view linked providers and disconnect them with safety checks

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Button,
  IconButton,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress
} from '@mui/material';
import {
  Google,
  Facebook,
  Email as EmailIcon,
  Delete,
  Security,
  Link as LinkIcon
} from '@mui/icons-material';
import axios from 'axios';
import { getUserAuthProviders, removeAuthProvider } from '../services/api';
import { oauthBrandColors } from '../theme/tokens';
import { UserAuthProvider } from '../types/api';

interface AuthProviderManagerProps {
  onUpdate?: () => void;
}

const AuthProviderManager: React.FC<AuthProviderManagerProps> = ({ onUpdate }) => {
  const [authProviders, setAuthProviders] = useState<UserAuthProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [removing, setRemoving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    provider: UserAuthProvider | null;
  }>({ open: false, provider: null });

  const loadAuthProviders = async () => {
    try {
      setLoading(true);
      setError(null);
      const providers = await getUserAuthProviders();
      setAuthProviders(providers);
    } catch (err: unknown) {
      setError('Failed to load authentication providers');
      console.error('Error loading auth providers:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAuthProviders();
  }, []);

  const getProviderIcon = (provider: string) => {
    switch (provider.toLowerCase()) {
      case 'google':
        return <Google sx={{ color: oauthBrandColors.google }} />;
      case 'facebook':
        return <Facebook sx={{ color: oauthBrandColors.facebook }} />;
      case 'email':
        return <EmailIcon sx={{ color: 'text.secondary' }} />;
      default:
        return <LinkIcon sx={{ color: 'text.secondary' }} />;
    }
  };

  const getProviderDisplayName = (provider: string) => {
    switch (provider.toLowerCase()) {
      case 'google':
        return 'Google';
      case 'facebook':
        return 'Facebook';
      case 'email':
        return 'Email & Password';
      default:
        return provider;
    }
  };

  const handleRemoveClick = (provider: UserAuthProvider) => {
    setConfirmDialog({ open: true, provider });
  };

  const handleConfirmRemove = async () => {
    if (!confirmDialog.provider) return;

    try {
      setRemoving(confirmDialog.provider.authProvider);
      setError(null);
      
      await removeAuthProvider(confirmDialog.provider.authProvider);
      
      // Reload the providers list
      await loadAuthProviders();
      
      // Notify parent component
      if (onUpdate) {
        onUpdate();
      }
    } catch (err: unknown) {
      const message = axios.isAxiosError(err) ? err.response?.data?.message : undefined;
      setError(message || 'Failed to remove authentication provider');
    } finally {
      setRemoving(null);
      setConfirmDialog({ open: false, provider: null });
    }
  };

  const handleCancelRemove = () => {
    setConfirmDialog({ open: false, provider: null });
  };

  const renderSecondaryAction = (provider: UserAuthProvider) => (
    <Box display="flex" flexDirection="column" alignItems="center" sx={{ pt: 1, pb: 1 }}>
      <Chip
        label="Connected"
        size="small"
        color="success"
        variant="outlined"
        sx={{ mb: 0.5 }}
      />
      {provider.canRemove ? (
        <IconButton
          edge="end"
          aria-label="disconnect"
          onClick={() => handleRemoveClick(provider)}
          disabled={removing === provider.authProvider}
          size="small"
        >
          {removing === provider.authProvider ? (
            <CircularProgress size={20} />
          ) : (
            <Delete />
          )}
        </IconButton>
      ) : (
        <Typography variant="caption" color="text.secondary">
          Required
        </Typography>
      )}
    </Box>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <Security color="primary" />
        <Typography variant="h6">
          Authentication Methods
        </Typography>
      </Box>

      <Typography variant="body2" color="text.secondary" mb={2}>
        Manage how you sign in to your account. You must have at least one authentication method.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <List>
        {authProviders.map((provider) => (
          <ListItem
            key={provider.id}
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              mb: 1,
              backgroundColor: 'background.paper'
            }}
          >
            <ListItemIcon>
              {getProviderIcon(provider.authProvider)}
            </ListItemIcon>
            <ListItemText
              primary={getProviderDisplayName(provider.authProvider)}
              secondary={`Added ${new Date(provider.createdAt).toLocaleDateString()}`}
            />
            <ListItemSecondaryAction>
              {renderSecondaryAction(provider)}
            </ListItemSecondaryAction>
          </ListItem>
        ))}
      </List>

      {authProviders.length === 1 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            You have only one authentication method. To add more options, you can connect 
            social accounts by signing in with them using the same email address.
          </Typography>
        </Alert>
      )}

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={handleCancelRemove}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Remove Authentication Method
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to disconnect your {getProviderDisplayName(confirmDialog.provider?.authProvider || '')} account?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            You&apos;ll no longer be able to sign in using this method.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelRemove}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmRemove}
            color="error"
            variant="contained"
            disabled={removing !== null}
          >
            {removing ? 'Removing...' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default AuthProviderManager;